import React, { useState, useContext, useEffect } from "react";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	FormHelperText,
	IconButton,
	InputAdornment,
	TextField,
	Typography,
	alpha,
	getLuminance,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import "react-phone-input-2/lib/style.css";
import { useToast } from "../../contexts/ToastContext.js";
import ConnectionsClient from "../../services/ConnectionsClient.js";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../contexts/UserContext.js";
import AsyncStorage from "@react-native-async-storage/async-storage";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { CreatorContext } from "../../contexts/CreatorContext.js";
import CustomPhoneInput from "../CustomPhoneInput/CustomPhoneInput.js";

function Login({ inModal, callback, loginRoute = "login" }) {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [repeatPassword, setRepeatPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false); // Estado para mostrar/ocultar la contraseña

	const [isRegistering, setIsRegistering] = useState(loginRoute === "register"); // Modo de registro
	const { user, storeUser, getUser } = useContext(UserContext);
	const [phone, setPhone] = useState("");
	const [phonePrefix, setPhonePrefix] = useState("");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [name, setName] = useState("");
	const [checked, setChecked] = useState(false);
	const [errors, setErrors] = useState({
		email: "",
		password: "",
	});

	const [registerErrors, setRegisterErrors] = useState({
		name: "",
		email: "",
		password: "",
		repeatPassword: "",
	});

	const [loading, setLoading] = useState(false);

	const [registerLoading, setRegisterLoading] = useState(false);
	const [loginLoading, setLoginLoading] = useState(false);
	const isEmpty = (value) => value.trim() === ""; // Helper para verificar si está vacío

	const [verificationCode, setVerificationCode] = useState("");
	const [isConfirming, setIsConfirming] = useState(loginRoute === "activate"); // Modo de confirmación
	const navigate = useNavigate();
	const { basePath } = useParams();

	const { showMessage } = useToast();
	const { t } = useTranslation();

	useEffect(() => {
		setIsRegistering(loginRoute === "register");
		setIsConfirming(loginRoute === "activate");
	}, [loginRoute]);

	// Función para validar el correo electrónico
	const validateEmail = (email) => {
		const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return re.test(String(email).toLowerCase());
	};

	// Verificar si todos los campos de registro están completos
	const isRegisterDisabled =
		!email ||
		!password ||
		!phone ||
		!name ||
		!validateEmail(email) ||
		!repeatPassword;

	const handleLogin = () => {
		onSubmit();
	};

	const handleRegister = () => {
		onSubmitRegister();
	};

	const handlePhoneChange = (value, countryData) => {
		setPhone(value); // Número completo con el prefijo

		// Separar el prefijo y el número
		const prefix = `+${countryData.dialCode}`;
		const numberWithoutPrefix = value.replace(countryData.dialCode, "");

		setPhonePrefix(prefix); // Establecer el prefijo
		setPhoneNumber(numberWithoutPrefix); // Establecer el número sin prefijo
	};

	const validateCode = async (code, email) => {
		console.log(loading);
		setLoading(true);
		try {
			const token = await AsyncStorage.getItem("token");
			const headers = token ? { authorization: token, "x-wc-call": true } : {};
			await ConnectionsClient.get("code", {
				params: {
					code: code,
					email: user?.email || email,
					//appId: "58931d28-295b-4ba7-9f5a-4b6d0627d459",
				},
				headers: headers,
			})
				.then(async (res) => {
					setLoading(false);

					//getUser();
					storeUser({ ...user, activatedAt: new Date() });

					if (inModal) {
						if (callback) {
							callback();
						}
					} else {
						navigate(`/${basePath}/`);
					}
				})
				.catch((error) => {
					setLoading(false);
					console.log(error);
					throw error;
				});
		} catch (error) {
			console.log(error);
			let message = t("validateAccount.generalError");
			if (error.response?.data?.message === "INVALID_CODE") {
				message = t("validateAccount.invalidCode");
			}
			showMessage({
				message: message,
				type: "danger",
				icon: "danger",
			});
		}
	};

	const handleConfirmCode = () => {
		if (verificationCode.length === 6) {
			validateCode(verificationCode, email);
		} else {
			showMessage({
				message: t("errors.incorrectCode"),
				type: "danger",
				icon: "danger",
			});
		}
	};

	const handleResendCode = async () => {
		const token = await AsyncStorage.getItem("token");
		const headers = token ? { authorization: token, "x-wc-call": true } : {};
		ConnectionsClient.post(
			"/resendCode",
			{
				email: user?.email || email,
				//appId: "58931d28-295b-4ba7-9f5a-4b6d0627d459",
				codeType: "verification",
			},
			{
				headers: headers,
			}
		)
			.then((res) => {
				showMessage({
					message: t("validateAccount.codeResent"),
					type: "success",
					icon: "success",
				});
			})
			.catch((error) => {
				showMessage({
					message: t("validateAccount.generalError"),
					type: "danger",
					icon: "danger",
				});
			});
	};

	// Determinar si el botón de iniciar sesión debe estar habilitado
	const isLoginDisabled = !email || !password || !validateEmail(email);

	const onSubmit = async (authentication, socialType) => {
		let auxErrors = {
			email: "",
			password: "",
		};

		if (!authentication) {
			if (isEmpty(email)) {
				auxErrors.email = t("errors.emptyEmail");
			} else if (!validateEmail(email)) {
				auxErrors.email = t("errors.incorrectEmail");
			}

			if (isEmpty(password)) {
				auxErrors.password = t("errors.emptyPassword");
			}
		}

		setErrors(auxErrors);
		console.log(errors);

		let params = {};

		if (authentication) {
			ConnectionsClient.post("/login", {
				params: {
					//appId: "58931d28-295b-4ba7-9f5a-4b6d0627d459",
				},
			})
				.then(async (res) => {
					await AsyncStorage.setItem("token", res.data.token);
					storeUser(res.data.user);
					getUser();
					if (inModal) {
						if (callback) {
							callback();
						}
					} else {
						navigate(`/${basePath}/`);
					}
					setLoginLoading(false);
				})
				.catch((error) => {
					console.log(error);
					setLoginLoading(false);

					showMessage({
						message: t("errors.invalidCredentials"),
						type: "danger",
						icon: "danger",
					});
				});
		} else {
			params = {
				email: email,
				username: email,
				password: password,
				//appId: "58931d28-295b-4ba7-9f5a-4b6d0627d459",
			};

			if (auxErrors.email === "" && auxErrors.password === "") {
				ConnectionsClient.post("/login", params)
					.then(async (res) => {
						await AsyncStorage.setItem("token", res.data.token);
						storeUser(res.data.user);
						getUser();
						if (inModal) {
							if (callback) {
								callback();
							}
						} else {
							navigate(`/${basePath}/`);
						}
						setLoginLoading(false);
					})
					.catch((error) => {
						console.log(error);
						setLoginLoading(false);
						if (error?.response?.data?.message === "NOT_ACTIVATED") {
							setIsConfirming(true);
						} else {
							showMessage({
								message: t("errors.invalidCredentials"),
								type: "danger",
								icon: "danger",
							});
						}
					});
			}
		}
	};

	const validatePassword = (password, t) => {
		const errors = [];

		// Check password length
		if (password.length < 8) {
			errors.push(t("passwordInfo.lengthError"));
		}

		// Check for uppercase letter
		if (!/[A-Z]/.test(password)) {
			errors.push(t("passwordInfo.uppercaseError"));
		}

		// Check for lowercase letter
		if (!/[a-z]/.test(password)) {
			errors.push(t("passwordInfo.lowercaseError"));
		}

		// Check for a number
		if (!/\d/.test(password)) {
			errors.push(t("passwordInfo.digitError"));
		}

		// Check for a special character
		if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
			errors.push(t("passwordInfo.specialCharError"));
		}

		// If no errors, return true (valid password)
		if (errors.length === 0) {
			return true;
		}

		// Return array of error messages if invalid
		return errors;
	};

	const onSubmitRegister = async () => {
		try {
			let auxErrors = {
				name: "",
				email: "",
				password: "",
				repeatPassword: "",
			};

			// Validar nombre y correo electrónico
			if (isEmpty(name)) {
				auxErrors.name = t("errors.emptyName");
			}

			if (isEmpty(email)) {
				auxErrors.email = t("errors.emptyEmail");
			}

			// Validar contraseña
			if (isEmpty(password)) {
				auxErrors.password = t("errors.emptyPassword");
				showMessage({
					message: t("errors.emptyPassword"),
					type: "danger",
					icon: "danger",
				});
			} else {
				const validationResult = validatePassword(password, t);

				if (validationResult === true) {
					// Contraseña válida
				} else {
					// Mostrar un solo mensaje de error general
					showMessage({
						message: t("passwordInfo.generalRequirement"), // Mensaje general
						type: "danger",
						icon: "danger",
					});
					auxErrors.password = t("passwordInfo.generalRequirement");
				}
			}

			// Validar repetición de contraseña
			if (isEmpty(repeatPassword)) {
				auxErrors.repeatPassword = t("errors.emptyPassword");
				showMessage({
					message: t("errors.emptyPassword"),
					type: "danger",
					icon: "danger",
				});
			} else if (password !== repeatPassword) {
				auxErrors.repeatPassword = t("errors.differentPassword");
				showMessage({
					message: t("errors.differentPassword"),
					type: "danger",
					icon: "danger",
				});
			}

			console.log(registerErrors);

			// Si no hay errores, proceder con el registro
			if (
				auxErrors.name === "" &&
				auxErrors.email === "" &&
				auxErrors.password === "" &&
				auxErrors.repeatPassword === ""
			) {
				setRegisterLoading(true);
				const users = await ConnectionsClient.post("/users", {
					name: name,
					first_name: name,
					email: email,
					username: email,
					password: password,
					phone: phoneNumber,
					phonePrefix: phonePrefix,
					//appId: "58931d28-295b-4ba7-9f5a-4b6d0627d459",
				});

				AsyncStorage.setItem("token", users.data.token);
				storeUser(users.data.user);
				setRegisterLoading(false);
				setIsConfirming(true);
			} else {
				setRegisterErrors(auxErrors);
				console.log(auxErrors);
			}
		} catch (error) {
			setRegisterLoading(false);
			let message = t("errors.generalError");
			if (
				error.message === "EMAIL_ALREADY_EXISTS" ||
				error.response?.data?.message === "EMAIL_ALREADY_EXISTS" ||
				error.response?.status === 400
			) {
				message = t("errors.existingEmail");
			}
			showMessage({
				message: message,
				type: "danger",
				icon: "danger",
			});
		}
	};

	const { creator } = useContext(CreatorContext);

	return (
		<Box
			sx={{
				padding: { xs: "12px", sm: "20px" },
				overflow: inModal ? "auto" : undefined,
				width: inModal ? "100%" : undefined,
				height: inModal ? "auto" : "calc(100vh - 110px)",
				maxHeight: inModal ? "calc(100% - 40px)" : undefined,
				backgroundColor: creator?.backgroundColor || "#ffffff",
				color: creator?.fontColor || "#000000",
				"& .selected-flag:hover": {
					background: creator?.backgroundColor ?? "#FFFFFF",
				},
				"& .selected-flag:focus": {
					background: creator?.backgroundColor ?? "#FFFFFF",
				},
				"& .flag-dropdown.open .selected-flag": {
					background: creator?.backgroundColor ?? "#FFFFFF",
				},
				".country-list": {
					background: creator?.backgroundColor ?? "#FFFFFF",
				},
				".country-list .country:hover": {
					background: alpha(creator?.fontColor ?? "#FFFFFF", 0.2),
				},
				".country-list .country.highlight": {
					background: alpha(creator?.fontColor ?? "#FFFFFF", 0.25),
				},
				"& input:disabled": {
					borderColor:
						alpha(creator?.fontColor ?? "#000000", 0.2) + "!important",
					color: alpha(creator?.fontColor ?? "#000000", 0.2) + "!important",
				},
				"& input:disabled + .flag-dropdown:disabled": {
					borderColor:
						alpha(creator?.fontColor ?? "#000000", 0.2) + "!important",
					color: alpha(creator?.fontColor ?? "#000000", 0.2) + "!important",
				},
			}}
		>
			{!isConfirming ? (
				<>
					<Typography
						variant="h4"
						gutterBottom
						sx={{
							fontSize: "16px",
							fontWeight: 600,
							marginBottom: 2,
						}}
					>
						{isRegistering ? t("register") : t("login")}
					</Typography>

					{/* Formulario de Registro */}
					{isRegistering && (
						<>
							<TextField
								placeholder="Nombre"
								fullWidth
								value={name}
								onChange={(e) => setName(e.target.value)}
								sx={{
									marginBottom: "16px", // Margen consistente con otros inputs
									"& fieldset": {
										borderColor: alpha(creator?.fontColor ?? "#000000", 0.3),
									},
									"& .Mui-focused fieldset": {
										borderColor: alpha(creator?.mainColor ?? "#43BD98", 0.7), // Borde con transparencia al estar enfocado
									},
									"& input": {
										color: creator?.fontColor ?? "#000000", // Color de texto normal
										WebkitTextFillColor: creator?.fontColor ?? "#000000", // Compatibilidad con navegadores Webkit
									},
									"& .MuiInputBase-root:hover fieldset": {
										borderColor: alpha(creator?.mainColor ?? "#43BD98", 0.7), // Borde con transparencia al hacer hover
									},
									"& input:not(.Mui-disabled)": {
										color: creator?.fontColor ?? "#000000", // Color del texto cuando no está deshabilitado
										WebkitTextFillColor: creator?.fontColor ?? "#000000", // Compatibilidad Webkit
									},
									fontSize: "14px",
								}}
								inputProps={{
									sx: {
										"&::placeholder": {
											fontSize: "14px",
											color:
												alpha(creator?.fontColor ?? "#000000", 0.2) +
												"!important",
										},
										fontSize: "14px",
										color: creator?.fontColor ?? "#000000",
										"&:-webkit-autofill": {
											color: (creator?.fontColor ?? "#000000") + "!important",
											WebkitTextFillColor:
												(creator?.fontColor ?? "#000000") + "!important",
											backgroundColor:
												(creator?.backgroundColor ?? "#ffffff") + " !important",
											WebkitBoxShadow:
												"0 0 0 1000px " +
												(creator?.backgroundColor ?? "#ffffff") +
												" inset",
											fontSize: "14px",
											font: creator?.fontFamily ?? "Montserrat",
											fontFamily: `'${
												creator?.fontFamily ?? "Montserrat"
											}', sans-serif !important`,
										},
									},
								}}
							/>
							<TextField
								placeholder={t("email")}
								fullWidth
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								error={email && !validateEmail(email)}
								helperText={
									email && !validateEmail(email)
										? t("errors.incorrectEmail")
										: ""
								}
								sx={{
									marginBottom: "16px", // Margen consistente con otros inputs
									"& fieldset": {
										borderColor: alpha(creator?.fontColor ?? "#000000", 0.3),
									},
									"& .Mui-focused fieldset": {
										borderColor: alpha(creator?.mainColor ?? "#43BD98", 0.7), // Borde con transparencia al estar enfocado
									},
									"& input": {
										color: creator?.fontColor ?? "#000000", // Color de texto normal
										WebkitTextFillColor: creator?.fontColor ?? "#000000", // Compatibilidad con navegadores Webkit
									},
									"& .MuiInputBase-root:hover fieldset": {
										borderColor: alpha(creator?.mainColor ?? "#43BD98", 0.7), // Borde con transparencia al hacer hover
									},
									"& input:not(.Mui-disabled)": {
										color: creator?.fontColor ?? "#000000", // Color del texto cuando no está deshabilitado
										WebkitTextFillColor: creator?.fontColor ?? "#000000", // Compatibilidad Webkit
									},
									fontSize: "14px",
								}}
								inputProps={{
									sx: {
										"&::placeholder": {
											fontSize: "14px",
											color:
												alpha(creator?.fontColor ?? "#000000", 0.2) +
												"!important",
										},
										fontSize: "14px",
										color: creator?.fontColor ?? "#000000",
										"&:-webkit-autofill": {
											color: (creator?.fontColor ?? "#000000") + "!important",
											WebkitTextFillColor:
												(creator?.fontColor ?? "#000000") + "!important",
											backgroundColor:
												(creator?.backgroundColor ?? "#ffffff") + " !important",
											WebkitBoxShadow:
												"0 0 0 1000px " +
												(creator?.backgroundColor ?? "#ffffff") +
												" inset",
											fontSize: "14px",
											font: creator?.fontFamily ?? "Montserrat",
											fontFamily: `'${
												creator?.fontFamily ?? "Montserrat"
											}', sans-serif !important`,
										},
									},
								}}
							/>
							<CustomPhoneInput
								country={"es"}
								value={phone}
								onChange={handlePhoneChange}
								inputStyle={{
									width: "100%", // Asegurar que ocupe todo el ancho
									fontSize: "14px", // Tamaño de fuente del input
									fontFamily: "Montserrat, sans-serif", // Cambiar la fuente a Montserrat
									height: "56px", // Ajustar la altura del input para coincidir con otros inputs
									paddingLeft: "70px", // Aumentar el padding para evitar el solapamiento del prefijo
									marginBottom: "16px", // Asegurar margen consistente con otros inputs
									background: creator?.backgroundColor ?? "#FFFFFF",
									borderColor: alpha(creator?.fontColor ?? "#000000", 0.3),
									color: creator?.fontColor ?? "#000000",
								}}
								buttonStyle={{
									width: "56px", // Ajustar el ancho del área de la bandera
									height: "56px", // Asegurar que la bandera tenga la misma altura que el input
									background: creator?.backgroundColor ?? "#FFFFFF",
									borderColor: alpha(creator?.fontColor ?? "#000000", 0.3),
								}}
								dropdownStyle={{
									fontFamily: "Montserrat, sans-serif", // Cambiar la fuente de la lista desplegable
									fontSize: "14px", // Tamaño de fuente en el dropdown
								}}
							/>
							<TextField
								placeholder={t("password")}
								type={showPassword ? "text" : "password"} // Cambiar entre texto y contraseña
								fullWidth
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								sx={{
									marginTop: "16px",
									marginBottom: "16px", // Margen consistente con otros inputs
									"& fieldset": {
										borderColor: alpha(creator?.fontColor ?? "#000000", 0.3),
									},
									"& .Mui-focused fieldset": {
										borderColor: alpha(creator?.mainColor ?? "#43BD98", 0.7), // Borde con transparencia al estar enfocado
									},
									"& input": {
										color: creator?.fontColor ?? "#000000", // Color de texto normal
										WebkitTextFillColor: creator?.fontColor ?? "#000000", // Compatibilidad con navegadores Webkit
									},
									"& .MuiInputBase-root:hover fieldset": {
										borderColor: alpha(creator?.mainColor ?? "#43BD98", 0.7), // Borde con transparencia al hacer hover
									},
									"& input:not(.Mui-disabled)": {
										color: creator?.fontColor ?? "#000000", // Color del texto cuando no está deshabilitado
										WebkitTextFillColor: creator?.fontColor ?? "#000000", // Compatibilidad Webkit
									},
									fontSize: "14px",
								}}
								inputProps={{
									sx: {
										"&::placeholder": {
											fontSize: "14px",
											color:
												alpha(creator?.fontColor ?? "#000000", 0.2) +
												"!important",
										},
										fontSize: "14px",
										color: creator?.fontColor ?? "#000000",
										"&:-webkit-autofill": {
											color: (creator?.fontColor ?? "#000000") + "!important",
											WebkitTextFillColor:
												(creator?.fontColor ?? "#000000") + "!important",
											backgroundColor:
												(creator?.backgroundColor ?? "#ffffff") + " !important",
											WebkitBoxShadow:
												"0 0 0 1000px " +
												(creator?.backgroundColor ?? "#ffffff") +
												" inset",
											fontSize: "14px",
											font: creator?.fontFamily ?? "Montserrat",
											fontFamily: `'${
												creator?.fontFamily ?? "Montserrat"
											}', sans-serif !important`,
										},
									},
								}}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												onClick={() => {
													setShowPassword((prev) => !prev);
												}}
												sx={{
													color:
														alpha(creator?.fontColor ?? "#000000", 0.7) +
														"!important",
												}}
											>
												{showPassword ? (
													<VisibilityOff sx={{ fontSize: "14px" }} />
												) : (
													<Visibility sx={{ fontSize: "14px" }} />
												)}
											</IconButton>
										</InputAdornment>
									),
								}}
							/>

							<TextField
								placeholder={t("repeatPassword")}
								type={showPassword ? "text" : "password"} // Cambiar entre texto y contraseña
								fullWidth
								value={repeatPassword}
								onChange={(e) => setRepeatPassword(e.target.value)}
								sx={{
									marginBottom: "16px", // Margen consistente con otros inputs
									"& fieldset": {
										borderColor: alpha(creator?.fontColor ?? "#000000", 0.3),
									},
									"& .Mui-focused fieldset": {
										borderColor: alpha(creator?.mainColor ?? "#43BD98", 0.7), // Borde con transparencia al estar enfocado
									},
									"& input": {
										color: creator?.fontColor ?? "#000000", // Color de texto normal
										WebkitTextFillColor: creator?.fontColor ?? "#000000", // Compatibilidad con navegadores Webkit
									},
									"& .MuiInputBase-root:hover fieldset": {
										borderColor: alpha(creator?.mainColor ?? "#43BD98", 0.7), // Borde con transparencia al hacer hover
									},
									"& input:not(.Mui-disabled)": {
										color: creator?.fontColor ?? "#000000", // Color del texto cuando no está deshabilitado
										WebkitTextFillColor: creator?.fontColor ?? "#000000", // Compatibilidad Webkit
									},
									fontSize: "14px",
								}}
								inputProps={{
									sx: {
										"&::placeholder": {
											fontSize: "14px",
											color:
												alpha(creator?.fontColor ?? "#000000", 0.2) +
												"!important",
										},
										fontSize: "14px",
										color: creator?.fontColor ?? "#000000",
										"&:-webkit-autofill": {
											color: (creator?.fontColor ?? "#000000") + "!important",
											WebkitTextFillColor:
												(creator?.fontColor ?? "#000000") + "!important",
											backgroundColor:
												(creator?.backgroundColor ?? "#ffffff") + " !important",
											WebkitBoxShadow:
												"0 0 0 1000px " +
												(creator?.backgroundColor ?? "#ffffff") +
												" inset",
											fontSize: "14px",
											font: creator?.fontFamily ?? "Montserrat",
											fontFamily: `'${
												creator?.fontFamily ?? "Montserrat"
											}', sans-serif !important`,
										},
									},
								}}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												onClick={() => {
													setShowPassword((prev) => !prev);
												}}
												sx={{
													color:
														alpha(creator?.fontColor ?? "#000000", 0.7) +
														"!important",
												}}
											>
												{showPassword ? (
													<VisibilityOff sx={{ fontSize: "14px" }} />
												) : (
													<Visibility sx={{ fontSize: "14px" }} />
												)}
											</IconButton>
										</InputAdornment>
									),
								}}
							/>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									color:
										alpha(creator?.fontColor ?? "#000000", 0.7) + "!important",
									"& a": {
										color: (creator?.mainColor ?? "#000000") + "!important",
									},
									"& a:visited": {
										color: (creator?.mainColor ?? "#000000") + "!important",
									},
									"& a:website-any-link": {
										color: (creator?.mainColor ?? "#000000") + "!important",
									},
								}}
							>
								<Checkbox
									checked={checked}
									onChange={(event) => {
										setChecked(event.target.checked);
									}}
									sx={{
										color:
											alpha(creator?.fontColor ?? "#000000", 0.7) +
											"!important",
										"& a": {
											color: (creator?.mainColor ?? "#000000") + "!important",
										},
										"& a:visited": {
											color: (creator?.mainColor ?? "#000000") + "!important",
										},
										"& a:website-any-link": {
											color: (creator?.mainColor ?? "#000000") + "!important",
										},
									}}
									color="primary"
								/>
								<FormHelperText
									component="div"
									sx={{
										color:
											alpha(creator?.fontColor ?? "#000000", 0.7) +
											"!important",
										fontSize: "11px",
									}}
								>
									<>
										{t("terms")}{" "}
										<a
											href={creator?.termsURL}
											target="_blank"
											rel="noreferrer"
											onClick={(e) => e.stopPropagation()}
										>
											{t("terms2")}
										</a>{" "}
										{t("terms3")}{" "}
										<a
											href={creator?.policyURL}
											target="_blank"
											rel="noreferrer"
											onClick={(e) => e.stopPropagation()}
										>
											{t("terms4")}
										</a>
									</>
								</FormHelperText>
							</Box>
						</>
					)}

					{/* Formulario de Inicio de Sesión */}
					{!isRegistering && (
						<>
							<TextField
								placeholder={t("email")}
								fullWidth
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								error={email && !validateEmail(email)}
								helperText={
									email && !validateEmail(email) ? "Correo no válido" : ""
								}
								sx={{
									marginBottom: "16px", // Margen consistente con otros inputs
									"& fieldset": {
										borderColor: alpha(creator?.fontColor ?? "#000000", 0.3),
									},
									"& .Mui-focused fieldset": {
										borderColor: alpha(creator?.mainColor ?? "#43BD98", 0.7), // Borde con transparencia al estar enfocado
									},
									"& input": {
										color: creator?.fontColor ?? "#000000", // Color de texto normal
										WebkitTextFillColor: creator?.fontColor ?? "#000000", // Compatibilidad con navegadores Webkit
									},
									"& .MuiInputBase-root:hover fieldset": {
										borderColor: alpha(creator?.mainColor ?? "#43BD98", 0.7), // Borde con transparencia al hacer hover
									},
									"& input:not(.Mui-disabled)": {
										color: creator?.fontColor ?? "#000000", // Color del texto cuando no está deshabilitado
										WebkitTextFillColor: creator?.fontColor ?? "#000000", // Compatibilidad Webkit
									},
									fontSize: "14px",
								}}
								inputProps={{
									sx: {
										"&::placeholder": {
											fontSize: "14px",
											color:
												alpha(creator?.fontColor ?? "#000000", 0.2) +
												"!important",
										},
										fontSize: "14px",
										color: creator?.fontColor ?? "#000000",
										"&:-webkit-autofill": {
											color: (creator?.fontColor ?? "#000000") + "!important",
											WebkitTextFillColor:
												(creator?.fontColor ?? "#000000") + "!important",
											backgroundColor:
												(creator?.backgroundColor ?? "#ffffff") + " !important",
											WebkitBoxShadow:
												"0 0 0 1000px " +
												(creator?.backgroundColor ?? "#ffffff") +
												" inset",
											fontSize: "14px",
											font: creator?.fontFamily ?? "Montserrat",
											fontFamily: `'${
												creator?.fontFamily ?? "Montserrat"
											}', sans-serif !important`,
										},
									},
								}}
							/>
							<TextField
								placeholder={t("password")}
								type="password"
								fullWidth
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								sx={{
									marginBottom: "16px", // Margen consistente con otros inputs
									"& fieldset": {
										borderColor: alpha(creator?.fontColor ?? "#000000", 0.3),
									},
									"& .Mui-focused fieldset": {
										borderColor: alpha(creator?.mainColor ?? "#43BD98", 0.7), // Borde con transparencia al estar enfocado
									},
									"& input": {
										color: creator?.fontColor ?? "#000000", // Color de texto normal
										WebkitTextFillColor: creator?.fontColor ?? "#000000", // Compatibilidad con navegadores Webkit
									},
									"& .MuiInputBase-root:hover fieldset": {
										borderColor: alpha(creator?.mainColor ?? "#43BD98", 0.7), // Borde con transparencia al hacer hover
									},
									"& input:not(.Mui-disabled)": {
										color: creator?.fontColor ?? "#000000", // Color del texto cuando no está deshabilitado
										WebkitTextFillColor: creator?.fontColor ?? "#000000", // Compatibilidad Webkit
									},
									fontSize: "14px",
								}}
								inputProps={{
									sx: {
										"&::placeholder": {
											fontSize: "14px",
											color:
												alpha(creator?.fontColor ?? "#000000", 0.2) +
												"!important",
										},
										fontSize: "14px",
										color: creator?.fontColor ?? "#000000",
										"&:-webkit-autofill": {
											color: (creator?.fontColor ?? "#000000") + "!important",
											WebkitTextFillColor:
												(creator?.fontColor ?? "#000000") + "!important",
											backgroundColor:
												(creator?.backgroundColor ?? "#ffffff") + " !important",
											WebkitBoxShadow:
												"0 0 0 1000px " +
												(creator?.backgroundColor ?? "#ffffff") +
												" inset",
											fontSize: "14px",
											font: creator?.fontFamily ?? "Montserrat",
											fontFamily: `'${
												creator?.fontFamily ?? "Montserrat"
											}', sans-serif !important`,
										},
									},
								}}
							/>
						</>
					)}

					<Box
						sx={{
							display: "flex",
							justifyContent: "flex-end",
							margin: "8px",
							marginTop: "6px",
							flexDirection: {
								xs: "column", // Para pantallas pequeñas (se apilan verticalmente)
								sm: "column", // Para pantallas grandes (se alinean horizontalmente)
							},
						}}
					>
						<Box
							sx={{
								textAlign: "center",
								paddingBottom: {
									xs: 0,
									sm: 0,
								},
							}}
						>
							{isRegistering ? (
								<Button
									variant="contained"
									color="primary"
									onClick={handleRegister}
									sx={{
										width: { xs: "100%", sm: "auto" },
										minHeight: 42.5,
										color: registerLoading
											? "transparent!important"
											: undefined,
										"&.Mui-disabled": {
											opacity: 0.5,
											color: registerLoading
												? "transparent!important"
												: getLuminance(creator?.mainColor ?? "#ffffff") >= 0.5
												? "#000000"
												: "#FFFFFF", // Texto de los botones primarios
											backgroundColor: creator?.mainColor ?? "#43BD98",
										},
									}}
									disabled={
										isRegisterDisabled ||
										registerLoading ||
										(isRegistering && !checked)
									}
								>
									{t("register2")} {/* Traducción para "Registrarse" */}
									{registerLoading && (
										<CircularProgress
											size={12}
											sx={{ position: "absolute", top: 16, color: "white" }}
										/>
									)}
								</Button>
							) : (
								<Button
									variant="contained"
									color="primary"
									onClick={handleLogin}
									sx={{
										width: { xs: "100%", sm: "auto" },
										minHeight: 42.5,
										color: loginLoading ? "transparent!important" : undefined,
										"&.Mui-disabled": {
											opacity: 0.5,
											color: loginLoading
												? "transparent!important"
												: getLuminance(creator?.mainColor ?? "#ffffff") >= 0.5
												? "#000000"
												: "#FFFFFF", // Texto de los botones primarios
											backgroundColor: creator?.mainColor ?? "#43BD98",
										},
									}}
									disabled={isLoginDisabled || loginLoading}
								>
									{t("login")} {/* Traducción para "Iniciar Sesión" */}
									{registerLoading && (
										<CircularProgress
											size={12}
											sx={{ position: "absolute", top: 16, color: "white" }}
										/>
									)}
								</Button>
							)}
						</Box>
						<Box
							sx={{
								textAlign: "center",
								marginTop: 1,
								width: { xs: "100%", sm: "100%" },
							}}
						>
							<Button
								variant="text"
								color="primary"
								onClick={() => setIsRegistering(!isRegistering)}
								sx={{ width: { xs: "100%", sm: "100%" }, fontSize: "11px" }}
							>
								{isRegistering ? t("switchToLogin") : t("switchToRegister")}{" "}
							</Button>
						</Box>
					</Box>
				</>
			) : (
				<>
					<Typography
						variant="h4"
						gutterBottom
						sx={{
							fontSize: "16px",
							fontWeight: 600,
							marginBottom: 2,
						}}
					>
						{t("confirmTitle")}
					</Typography>
					<TextField
						placeholder={t("verificationCodePlaceholder")}
						fullWidth
						value={verificationCode}
						onChange={(e) => setVerificationCode(e.target.value)}
						sx={{
							marginBottom: "16px", // Margen consistente con otros inputs
							"& fieldset": {
								borderColor: alpha(creator?.fontColor ?? "#000000", 0.3),
							},
							"& .Mui-focused fieldset": {
								borderColor: alpha(creator?.mainColor ?? "#43BD98", 0.7), // Borde con transparencia al estar enfocado
							},
							"& input": {
								color: creator?.fontColor ?? "#000000", // Color de texto normal
								WebkitTextFillColor: creator?.fontColor ?? "#000000", // Compatibilidad con navegadores Webkit
							},
							"& .MuiInputBase-root:hover fieldset": {
								borderColor: alpha(creator?.mainColor ?? "#43BD98", 0.7), // Borde con transparencia al hacer hover
							},
							"& input:not(.Mui-disabled)": {
								color: creator?.fontColor ?? "#000000", // Color del texto cuando no está deshabilitado
								WebkitTextFillColor: creator?.fontColor ?? "#000000", // Compatibilidad Webkit
							},
						}}
						inputProps={{
							sx: {
								"&::placeholder": {
									fontSize: "14px",
									color:
										alpha(creator?.fontColor ?? "#000000", 0.2) + "!important",
								},
								fontSize: "14px",
								color: creator?.fontColor ?? "#000000",
								"&:-webkit-autofill": {
									color: (creator?.fontColor ?? "#000000") + "!important",
									WebkitTextFillColor:
										(creator?.fontColor ?? "#000000") + "!important",
									backgroundColor:
										(creator?.backgroundColor ?? "#ffffff") + " !important",
									WebkitBoxShadow:
										"0 0 0 1000px " +
										(creator?.backgroundColor ?? "#ffffff") +
										" inset",
									fontSize: "14px",
									font: creator?.fontFamily ?? "Montserrat",
									fontFamily: `'${
										creator?.fontFamily ?? "Montserrat"
									}', sans-serif !important`,
								},
							},
						}}
					/>

					<Box
						sx={{
							margin: "8px",
							display: "flex",
							flexDirection: "column",
							alignItems: "flex-end", // Align to the left for both button and text
							gap: 1, // Space between the button and text
						}}
					>
						<Button
							variant="contained"
							color="primary"
							onClick={handleConfirmCode}
							disabled={!verificationCode}
							sx={{
								width: "auto",
								"&.Mui-disabled": {
									opacity: 0.5,
									color:
										getLuminance(creator?.mainColor ?? "#ffffff") >= 0.5
											? "#000000"
											: "#FFFFFF", // Texto de los botones primarios
									backgroundColor: creator?.mainColor ?? "#43BD98",
								},
							}} // Keep the button width auto
						>
							{t("confirmCode")}
						</Button>

						<Typography
							onClick={handleResendCode}
							sx={{
								fontSize: 12,
								cursor: "pointer",
								textAlign: "left",
								marginTop: "2px",
							}} // Align text to the left
						>
							{t("resendCode")}
						</Typography>
					</Box>
				</>
			)}
		</Box>
	);
}

export default Login;
